/**
 * Composite power rating.
 *
 * A single 0-100 number per manager, built from four weighted parts:
 *
 *   record        40   win percentage, straight through
 *   differential  25   points per game, clamped to ±21 either way
 *   schedule      20   your opponents' win percentage this season
 *   division      15   where you sit in your own division
 *
 * The weights and the ±21 clamp come from the league's own proposal.
 *
 * **Schedule is this season's, and only the games played.** It used to read how
 * each opponent finished the *previous* competition, across the whole fixture
 * list — which scored Season 2 on Season 1's table, and counted the four
 * managers new to the league as last-placed however they were actually
 * playing. Now it is the opponent win % the standings already compute for the
 * Rev 2.0 tiebreaker: the mean win % of everyone you have played, weighted per
 * game. Scored the same way record is — a share of the weight — so a .550
 * schedule earns 11 of 20. A manager yet to play counts as .500, level with the
 * league, rather than scoring nothing for a schedule that hasn't happened.
 *
 * **A season without divisions drops that part and rescales.** Season 1 was one
 * table, so "divisional rank" would just be league rank, which is mostly the
 * win percentage counted a second time. Those seasons are scored out of the
 * remaining 85 and scaled back up to 100.
 */
export const POWER_WEIGHTS = {
    record: 40,
    differential: 25,
    schedule: 20,
    division: 15,
};
/** Points per game beyond which extra margin stops earning credit. */
export const DIFF_CAP = 21;
/** What an unplayed schedule counts as: level with the league. */
export const NEUTRAL_SCHEDULE = 0.5;
const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
/** @param entrants everyone in the competition, with their record so far */
export function computePowerRatings(entrants) {
    const hasDivisions = entrants.some((e) => e.divisionRank != null);
    // Scoring out of 85 and scaling up keeps every season on one 0-100 scale.
    const noDivisionScale = 100 /
        (POWER_WEIGHTS.record + POWER_WEIGHTS.differential + POWER_WEIGHTS.schedule);
    const ratings = entrants.map((e) => {
        const recordPoints = e.winPct * POWER_WEIGHTS.record;
        const diffPerGame = e.gamesPlayed > 0 ? e.pointsDiff / e.gamesPlayed : 0;
        const capped = clamp(diffPerGame, -DIFF_CAP, DIFF_CAP);
        const differentialPoints = ((capped + DIFF_CAP) / (DIFF_CAP * 2)) * POWER_WEIGHTS.differential;
        const opponentWinPct = e.gamesPlayed > 0 ? e.opponentWinPct : null;
        const schedulePoints = clamp(opponentWinPct ?? NEUTRAL_SCHEDULE, 0, 1) * POWER_WEIGHTS.schedule;
        const divisionPoints = e.divisionRank == null || e.divisionSize < 2
            ? null
            : ((e.divisionSize - clamp(e.divisionRank, 1, e.divisionSize)) /
                (e.divisionSize - 1)) *
                POWER_WEIGHTS.division;
        const parts = recordPoints + differentialPoints + schedulePoints;
        const rating = hasDivisions
            ? parts + (divisionPoints ?? 0)
            : parts * noDivisionScale;
        return {
            managerId: e.managerId,
            recordPoints,
            differentialPoints,
            schedulePoints,
            divisionPoints: hasDivisions ? (divisionPoints ?? 0) : null,
            rating,
            diffPerGame,
            opponentWinPct,
        };
    });
    return ratings.sort((a, b) => b.rating - a.rating || a.managerId - b.managerId);
}
//# sourceMappingURL=power.js.map